import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";

import { Activity, FolderKanban, Plug, ScrollText, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import TraceScreen from "@/components/agents/code-companion/TraceScreen";
import EvidenceScreen from "@/components/agents/code-companion/EvidenceScreen";
import PackagePicker from "@/components/agents/code-companion/PackagePicker";
import GatePanel from "@/components/agents/code-companion/GatePanel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DevsScreen } from "@/components/agents/code-companion/DevsScreen";
import { SetupScreen } from "@/components/agents/code-companion/SetupScreen";
import { useCurrentUser } from "@/hooks/useAuth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { CcFlag, CcPackageStatus, CcPackageSummary } from "@/api/code-companion";
import {
  CC_COLUMNS,
  CC_FLOW_COLUMNS,
  computeBoardKpis,
  computeDevLoad,
  groupByStatus,
  openFlagsByPackage,
  resolveDrop,
  summarizeBatch,
} from "@/lib/code-companion-board";
import {
  activityDescriptor,
  formatRelativeTime,
  type ActivityKind,
} from "@/lib/code-companion-activity";
import {
  useCcActivity,
  useCcAddSupplement,
  useCcApproveContext,
  useCcArtifacts,
  useCcChangeSets,
  useCcCreateRun,
  useCcPackageRun,
  useCcContext,
  useCcDevelopers,
  useCcFlags,
  useCcMarkReady,
  useCcPackage,
  useCcPackages,
  useCcRebuildContext,
  useCcResolveFlag,
  useCcSupplements,
  useCcSetAssignee,
  useCcSetStatus,
} from "@/hooks/useCodeCompanion";
import { tp } from "@/lib/plural";

const UNASSIGNED = "__unassigned__";

// Mapa status → token semântico do DS (var CSS). Usamos hsl(var(--x)/α) inline
// (como o design), pois os tokens não têm placeholder de alpha no Tailwind.
const STATUS_VAR: Record<CcPackageStatus, string> = {
  backlog: "--muted-foreground",
  approved: "--success",
  in_progress: "--warning",
  ready_for_deploy: "--info",
  done: "--success",
  blocked: "--critical",
};
const tint = (s: CcPackageStatus) => ({
  background: `hsl(var(${STATUS_VAR[s]}) / 0.12)`,
  color: `hsl(var(${STATUS_VAR[s]}))`,
});
const solid = (s: CcPackageStatus) => ({ background: `hsl(var(${STATUS_VAR[s]}))` });

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function Avatar({ label }: { label: string }) {
  return (
    <div
      title={label}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-extrabold"
      style={{ background: "hsl(var(--primary))", color: "hsl(var(--accent))" }}
    >
      {initials(label)}
    </div>
  );
}

function StatusBadge({ status, label }: { status: CcPackageStatus; label: string }) {
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-bold"
      style={tint(status)}
    >
      {label}
    </span>
  );
}

function KpiTile({
  label,
  value,
  sub,
  bar,
}: {
  label: string;
  value: string | number;
  sub?: string;
  bar?: { pct: number; status: CcPackageStatus };
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-2 font-brand text-2xl font-bold text-primary">{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-muted-foreground">{sub}</div>}
      {bar && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full"
            style={{ width: `${bar.pct}%`, ...solid(bar.status) }}
          />
        </div>
      )}
    </div>
  );
}

function PackageCard({
  pkg,
  flags,
  devLabel,
  onOpen,
  readOnly = false,
}: {
  pkg: CcPackageSummary;
  flags: number;
  devLabel: string | null;
  onOpen: () => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: pkg.id,
    disabled: readOnly,
  });
  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, borderLeftColor: `hsl(var(${STATUS_VAR[pkg.status]}))` }}
      {...listeners}
      {...attributes}
      onClick={onOpen}
      // O dnd-kit já põe role="button" e tabIndex=0 via `attributes`, então o card se
      // ANUNCIA como botão. Sem isto ele não responde ao Enter, e como abrir um card é o
      // gesto que faz as tabs Trace e Evidence aparecerem, o teclado não chegava lá.
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`mb-2 w-full rounded-lg border border-l-[3px] bg-card p-3 text-left shadow-sm hover:border-primary ${
        readOnly ? "cursor-pointer" : "cursor-grab"
      } ${isDragging ? "opacity-60" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-xs font-semibold text-primary">{pkg.devclass}</span>
      </div>
      <div className="mt-1.5 text-[11px] text-muted-foreground">
        {t("{{n}} of {{total}} objects in development scope", {
          n: pkg.in_scope_count,
          total: pkg.object_count,
        })}
      </div>
      {pkg.progress_pct > 0 && (
        <div className="mt-2">
          <div className="h-1 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full"
              style={{ width: `${pkg.progress_pct}%`, background: "hsl(var(--info))" }}
            />
          </div>
          <div className="mt-0.5 text-right text-[9px] font-semibold text-muted-foreground">
            {pkg.progress_pct}%
          </div>
        </div>
      )}
      <div className="mt-2.5 flex items-center justify-between gap-2">
        {/* O `+` num circulo tracejado prometia "adicionar responsavel" e nao era
            acao nenhuma: o card inteiro so abre o painel do pacote. Sem responsavel
            a palavra diz o estado, e com responsavel o nome aparece junto da inicial
            — antes so o tooltip contava quem era. */}
        {devLabel ? (
          <span className="flex min-w-0 items-center gap-1.5">
            <Avatar label={devLabel} />
            <span className="truncate text-[11px] text-muted-foreground">{devLabel}</span>
          </span>
        ) : (
          <span className="text-[11px] italic text-muted-foreground">{t("Unassigned")}</span>
        )}
        {flags > 0 && (
          <span
            className="flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold"
            style={tint("blocked")}
          >
            ⚑ {flags}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Faixa de bloqueados: largura cheia, abaixo do fluxo.
 *
 * Continua sendo alvo de drop — bloquear um pacote e arrastar pra ca — mas nao
 * disputa espaco horizontal com as etapas. Vazia ela encolhe pra uma linha; com
 * pacote dentro ela chama atencao, que e o comportamento que se espera de uma
 * coluna de excecao.
 */
function BlockedLane({
  items,
  openFlags,
  devLabelOf,
  onOpen,
  readOnly = false,
}: {
  items: CcPackageSummary[];
  openFlags: Map<number, number>;
  devLabelOf: (id: string | null) => string | null;
  onOpen: (id: number) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: "blocked" });
  const cheia = items.length > 0;
  return (
    <div
      ref={setNodeRef}
      className={`mt-1 rounded-xl border border-dashed p-3 transition-colors ${
        isOver
          ? "border-primary bg-primary/5"
          : cheia
            ? "border-destructive/40 bg-destructive/5"
            : "border-border"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full" style={solid("blocked")} />
        <span className="text-xs font-bold text-foreground">{t("Blocked")}</span>
        <span className="rounded-full bg-secondary px-2 text-[11px] font-semibold text-muted-foreground">
          {items.length}
        </span>
        {!cheia && (
          <span className="text-[11px] text-muted-foreground">
            {t("nothing blocked · drop a package here to flag it")}
          </span>
        )}
      </div>
      {cheia && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((p) => (
            <PackageCard
              key={p.id}
              pkg={p}
              flags={openFlags.get(p.id) ?? 0}
              devLabel={devLabelOf(p.assigned_dev_id)}
              onOpen={() => onOpen(p.id)}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Column({
  status,
  label,
  items,
  openFlags,
  devLabelOf,
  onOpen,
  readOnly = false,
}: {
  status: CcPackageStatus;
  label: string;
  items: CcPackageSummary[];
  openFlags: Map<number, number>;
  devLabelOf: (id: string | null) => string | null;
  onOpen: (id: number) => void;
  readOnly?: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    // Cresce pra ocupar a largura disponivel e nunca encolhe abaixo de 230px: em
    // tela larga as cinco etapas cabem inteiras, em tela estreita a rolagem
    // horizontal volta a ser o plano B em vez de ser o padrao.
    <div className="shrink-0 grow basis-[230px]">
      <div className="mb-2 flex items-center gap-2 px-1">
        <span className="h-2 w-2 rounded-full" style={solid(status)} />
        <span className="text-xs font-bold text-foreground">{label}</span>
        <span className="rounded-full bg-secondary px-2 text-[11px] font-semibold text-muted-foreground">
          {items.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={`min-h-24 rounded-xl border border-dashed p-2 transition-colors ${
          isOver ? "border-primary bg-primary/5" : "border-border"
        }`}
      >
        {items.map((p) => (
          <PackageCard
            key={p.id}
            pkg={p}
            flags={openFlags.get(p.id) ?? 0}
            devLabel={devLabelOf(p.assigned_dev_id)}
            onOpen={() => onOpen(p.id)}
            readOnly={readOnly}
          />
        ))}
        {!items.length && (
          <div className="rounded-lg border border-dashed p-4 text-center text-[11px] text-muted-foreground">
            —
          </div>
        )}
      </div>
    </div>
  );
}

function BoardScreen() {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const pid = projectId ?? "";
  // Board read-only pro dev: vê pacotes/progresso/contexto, sem ações de owner.
  const currentUser = useCurrentUser();
  const isOwner = currentUser?.role === "admin";

  const packagesQ = useCcPackages(projectId);
  const flagsQ = useCcFlags(projectId, "open");
  const activityQ = useCcActivity(projectId, 50);
  const developersQ = useCcDevelopers(projectId);

  const approve = useCcApproveContext(pid);
  const assign = useCcSetAssignee(pid);
  const ready = useCcMarkReady(pid);
  const setStatus = useCcSetStatus(pid);
  const resolveFlag = useCcResolveFlag(pid);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  // O foco é do shell: escolher um pacote no board é escolher o assunto das tabs Trace
  // e Evidence, e as três precisam concordar sobre qual é.
  const foco = useContext(CcFocusContext);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [batching, setBatching] = useState(false);
  const detailQ = useCcPackage(projectId, selectedId ?? undefined);
  // O mais recente primeiro: a op ordena por id DESC.
  const artifactsQ = useCcArtifacts(projectId, selectedId ?? undefined);
  const contextQ = useCcContext(projectId, selectedId ?? undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const devLabelOf = useMemo(() => {
    const map = new Map<string, string>();
    // Nome vem no próprio pacote (funciona pro dev read-only, sem o endpoint admin).
    for (const p of packagesQ.data?.items ?? []) {
      if (p.assigned_dev_id && p.assigned_dev_name) map.set(p.assigned_dev_id, p.assigned_dev_name);
    }
    // Roster (owner) complementa/atualiza.
    for (const d of developersQ.data?.items ?? []) {
      map.set(d.user_id, d.name ?? d.email ?? d.user_id.slice(0, 8));
    }
    return (id: string | null) => (id ? map.get(id) ?? id.slice(0, 8) : null);
  }, [developersQ.data, packagesQ.data]);

  const allPackages = useMemo(() => packagesQ.data?.items ?? [], [packagesQ.data]);
  const packageLabel = useCallback(
    (id: number) => allPackages.find((p) => p.id === id)?.devclass ?? null,
    [allPackages],
  );

  /**
   * O verbo do evento. Fica aqui, com a chave escrita a mao caso a caso, porque o
   * guarda de i18n so enxerga chave escrita a mao: um mapa no lib sumiria da
   * varredura.
   */
  function activityPhrase(kind: ActivityKind, raw: string): string {
    switch (kind) {
      case "assigned":
        return t("Assigned");
      case "blocker_raised":
        return t("Blocker raised on");
      case "context_approved":
        return t("Context approved");
      case "flag_raised":
        return t("Flag raised on");
      case "flag_resolved":
        return t("Flag resolved on");
      case "object_status":
        return t("Object advanced");
      case "progress":
        return t("Progress updated");
      case "ready_for_deploy":
        return t("Marked ready for deploy");
      case "status_change":
        return t("Moved");
      default:
        return raw;
    }
  }
  const openFlags = useMemo(
    () => openFlagsByPackage(flagsQ.data?.items ?? []),
    [flagsQ.data],
  );
  const packages = useMemo(
    () => (flaggedOnly ? allPackages.filter((p) => openFlags.get(p.id)) : allPackages),
    [allPackages, flaggedOnly, openFlags],
  );

  const byStatus = useMemo(() => groupByStatus(packages), [packages]);
  const kpis = useMemo(() => computeBoardKpis(allPackages), [allPackages]);
  const released = allPackages.filter((p) => p.status !== "backlog").length;

  const devLoad = useMemo(
    () => computeDevLoad(allPackages, developersQ.data?.items ?? []),
    [allPackages, developersQ.data],
  );

  // Pacotes em backlog aguardam o Gate B (aprovar contexto). O Gate A (escopo
  // técnico) já foi aprovado no Architect antes do board ser semeado.
  const backlogIds = (byStatus.get("backlog") ?? []).map((p) => p.id);
  const blocked = byStatus.get("blocked") ?? [];

  const selected = allPackages.find((p) => p.id === selectedId) ?? null;
  const selectedFlags = (flagsQ.data?.items ?? []).filter(
    (f) => f.package_id === selectedId && f.status === "open",
  );

  /**
   * Gate B em lote, com o toast preso ao resultado.
   *
   * Antes: `forEach(mutate)` e um toast verde no mesmo tick. O toast contava a
   * intencao, nao o que aconteceu — quarenta recusas apareciam como sucesso e o
   * board so desmentia depois de recarregar.
   */
  async function approveBacklog() {
    if (!backlogIds.length || batching) return;
    setBatching(true);
    try {
      const r = summarizeBatch(
        await Promise.allSettled(backlogIds.map((id) => approve.mutateAsync(id))),
      );
      if (r.allFailed) {
        toast.error(t("Could not approve any of the {{n}} packages", { n: r.failed }));
      } else if (r.failed) {
        toast.warning(
          t("{{ok}} approved · {{failed}} failed", { ok: r.ok, failed: r.failed }),
        );
      } else {
        toast.success(t("Context approved for {{n}} packages", { n: r.ok }));
      }
    } finally {
      setBatching(false);
    }
  }

  function onDragEnd(e: DragEndEvent) {
    const packageId = Number(e.active.id);
    const to = e.over?.id as CcPackageStatus | undefined;
    if (!to) return;
    const status = resolveDrop(allPackages, packageId, to);
    if (status) {
      setStatus.mutate(
        { packageId, status },
        { onError: () => toast.error(t("Could not move the package")) },
      );
    }
  }

  function statusLabel(s: CcPackageStatus): string {
    return t(CC_COLUMNS.find((c) => c.status === s)?.label ?? s);
  }

  return (
    <div className="space-y-5">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-brand text-xl font-semibold text-primary">
            {t("Migration Board")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Realize · custom-code migration by package · human always in control")}
          </p>
        </div>
      </div>

      <Tabs defaultValue="kanban">
        <TabsList>
          <TabsTrigger value="dashboard">{t("Dashboard")}</TabsTrigger>
          <TabsTrigger value="kanban">{t("Kanban")}</TabsTrigger>
        </TabsList>

        {/* ===== DASHBOARD ===== */}
        <TabsContent value="dashboard" className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <KpiTile
              label={t("Migrated")}
              value={`${kpis.pct}%`}
              sub={t("{{done}} of {{total}} packages", {
                done: kpis.migrated,
                total: kpis.total,
              })}
              bar={{ pct: kpis.pct, status: "done" }}
            />
            <KpiTile
              label={t("Released")}
              value={`${released} / ${kpis.total}`}
              sub={t("packages context-released")}
            />
            <KpiTile label={t("In progress")} value={kpis.inProgress} />
            <KpiTile
              label={t("Active devs")}
              value={`${devLoad.active} / ${devLoad.linked}`}
              sub={
                devLoad.unlinked.length
                  ? t("{{n}} assigned without project access", {
                      n: devLoad.unlinked.length,
                    })
                  : t("with a package · of those linked to the project")
              }
            />
            <KpiTile label={t("Open flags")} value={flagsQ.data?.total ?? 0} />
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {/* developer load */}
            <div className="rounded-xl border bg-card p-4">
              <div className="font-brand text-sm font-semibold text-primary">
                {t("Developer load")}
              </div>
              <div className="mt-4 space-y-3">
                {devLoad.rows.map((d) => (
                  <div key={d.id}>
                    <div className="mb-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar label={d.name} />
                        <span className="text-xs font-semibold">{d.name}</span>
                      </div>
                      <span className="text-[11px] text-muted-foreground">
                        {tp(t, d.n, "1 package", "{{n}} packages")}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${d.pct}%`, background: "hsl(var(--primary))" }}
                      />
                    </div>
                  </div>
                ))}
                {!devLoad.rows.length && (
                  <div className="text-[11px] text-muted-foreground">
                    {t("No developers linked to this project yet.")}
                  </div>
                )}
                {devLoad.unlinked.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between rounded-lg border border-amber-500/40 bg-amber-500/5 px-2.5 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar label={d.name} />
                      <span className="text-xs font-semibold">{d.name}</span>
                    </div>
                    <span className="text-[11px] text-amber-600 dark:text-amber-500">
                      {t("has packages but no project access")}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* recent activity */}
            <div className="rounded-xl border bg-card p-4">
              <div className="font-brand text-sm font-semibold text-primary">
                {t("Recent activity")}
              </div>
              <div className="mt-3 space-y-2">
                {(activityQ.data?.items ?? []).slice(0, 12).map((a) => {
                  const d = activityDescriptor(a, packageLabel);
                  return (
                    <div key={a.id} className="flex items-baseline justify-between gap-3 text-[11px]">
                      <span className="text-foreground">
                        {activityPhrase(d.kind, d.raw)}
                        {d.target && (
                          <span className="ml-1.5 font-medium">{d.target}</span>
                        )}
                        {d.detail && (
                          <span className="ml-1.5 text-muted-foreground">{d.detail}</span>
                        )}
                      </span>
                      <span
                        className="shrink-0 tabular-nums text-muted-foreground"
                        title={new Date(a.created_at).toLocaleString()}
                      >
                        {formatRelativeTime(a.created_at, new Date(), i18n.language)}
                      </span>
                    </div>
                  );
                })}
                {!activityQ.data?.items?.length && (
                  <div className="text-[11px] text-muted-foreground">
                    {t("No activity yet.")}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* risks */}
          <div className="rounded-xl border bg-card p-4">
            <div className="font-brand text-sm font-semibold text-primary">
              {t("Risks & attention")}
            </div>
            <div
              className="mt-2 text-[10px] font-bold uppercase tracking-wide"
              style={{ color: `hsl(var(--critical))` }}
            >
              {t("Blocked")} · {blocked.length}
            </div>
            <div className="mt-2 space-y-2">
              {blocked.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedId(p.id);
                    foco.focus(p);
                  }}
                  className="flex w-full items-center justify-between rounded-lg border p-2.5 text-left"
                  style={{
                    background: `hsl(var(--critical) / 0.06)`,
                    borderColor: `hsl(var(--critical) / 0.25)`,
                  }}
                >
                  <span className="font-mono text-xs font-semibold">{p.devclass}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {devLabelOf(p.assigned_dev_id) ?? t("Unassigned")}
                  </span>
                </button>
              ))}
              {!blocked.length && (
                <div className="text-[11px] text-muted-foreground">
                  {t("Nothing blocked.")}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ===== KANBAN ===== */}
        <TabsContent value="kanban" className="mt-4">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setFlaggedOnly((v) => !v)}
              className="rounded-lg border px-3 py-1.5 text-[11px] font-semibold"
              style={flaggedOnly ? tint("blocked") : undefined}
            >
              ⚑ {t("Flagged only")}
            </button>
            <div className="flex-1" />
            {/* Sem pacote no Backlog o botao sumia? Nao: ficava amarelo, em destaque,
                com `(0)`. CTA de destaque pra acao sem alvo treina o olho a ignorar
                o unico botao primario da tela. */}
            {isOwner && backlogIds.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    disabled={batching}
                    style={{
                      background: "hsl(var(--accent))",
                      color: "hsl(var(--accent-foreground))",
                    }}
                  >
                    {batching
                      ? t("Approving…")
                      : t("Approve context · Gate B ({{n}})", { n: backlogIds.length })}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t("Approve context for {{n}} packages?", { n: backlogIds.length })}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t(
                        "Gate B releases every package in Backlog for migration at once. Approving one by one lets you read each context first.",
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={approveBacklog}>
                      {t("Approve all")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          <DndContext sensors={sensors} onDragEnd={isOwner ? onDragEnd : undefined}>
            <div className="flex gap-3 overflow-x-auto pb-3">
              {CC_FLOW_COLUMNS.map((col) => (
                <Column
                  key={col.status}
                  status={col.status}
                  label={t(col.label)}
                  items={byStatus.get(col.status) ?? []}
                  openFlags={openFlags}
                  devLabelOf={devLabelOf}
                  onOpen={(id) => {
                    setSelectedId(id);
                    // Focar aqui é o que faz as tabs Trace e Evidence aparecerem no
                    // mesmo gesto: sem isso o dev abriria o card e não teria como
                    // saber que existe um destino para aquele pacote.
                    foco.focus(packages.find((p) => p.id === id) ?? null);
                  }}
                  readOnly={!isOwner}
                />
              ))}
            </div>

            <BlockedLane
              items={byStatus.get("blocked") ?? []}
              openFlags={openFlags}
              devLabelOf={devLabelOf}
              onOpen={(id) => {
                setSelectedId(id);
                foco.focus(packages.find((p) => p.id === id) ?? null);
              }}
              readOnly={!isOwner}
            />
          </DndContext>
        </TabsContent>
      </Tabs>

      {/* ===== DRAWER ===== */}
      <Sheet open={selectedId != null} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="w-[560px] overflow-y-auto sm:max-w-[560px]">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <span className="font-mono text-primary">{selected.devclass}</span>
                  <StatusBadge status={selected.status} label={statusLabel(selected.status)} />
                </SheetTitle>
                <SheetDescription>
                  {t("{{n}} of {{total}} objects in development scope", {
                    n: selected.in_scope_count,
                    total: selected.object_count,
                  })}
                </SheetDescription>
              </SheetHeader>

              {/* S-064 onda 7: o Trace virou destino. O que ficava aqui — faixa do
                  maestro, run, matriz, trilha, ordem, transporte, paridade — mora na tab
                  Trace, em largura inteira. Este Sheet volta a ser o que ele é bom em
                  ser: a administração do pacote (responsável, flags, contexto, artefatos,
                  promoção). Empilhar dez painéis numa gaveta estreita era a divergência
                  que se notava primeiro em relação ao protótipo. */}
              <div className="mt-4">
                <Button
                  className="w-full"
                  onClick={() => {
                    foco.focus(selected);
                    foco.goTo("trace");
                  }}
                >
                  {t("Open trace")}
                </Button>
              </div>

              <div className="mt-4 space-y-6">
                {isOwner && selected.status === "backlog" && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        style={{
                          background: "hsl(var(--accent))",
                          color: "hsl(var(--accent-foreground))",
                        }}
                      >
                        {t("Approve context")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("Approve context?")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("This releases the package to the developer via MCP.")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() =>
                            approve.mutate(selected.id, {
                              onSuccess: () => toast.success(t("Context approved")),
                              onError: () => toast.error(t("Could not approve context")),
                            })
                          }
                        >
                          {t("Approve")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}

                {/* assignee */}
                {isOwner && (
                  <div>
                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t("Assignee")}
                    </div>
                    <Select
                      value={selected.assigned_dev_id ?? UNASSIGNED}
                      onValueChange={(v) =>
                        assign.mutate({
                          packageId: selected.id,
                          userId: v === UNASSIGNED ? null : v,
                        })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t("Unassigned")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNASSIGNED}>{t("Unassigned")}</SelectItem>
                        {(developersQ.data?.items ?? []).map((d) => (
                          <SelectItem key={d.user_id} value={d.user_id}>
                            {d.name ?? d.email ?? d.user_id.slice(0, 8)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* flags */}
                {selectedFlags.length > 0 && (
                  <div>
                    <div
                      className="mb-2 text-[10px] font-bold uppercase tracking-wide"
                      style={{ color: `hsl(var(--critical))` }}
                    >
                      {t("Open flags")} · {selectedFlags.length}
                    </div>
                    <div className="space-y-2">
                      {selectedFlags.map((f) => (
                        <FlagCorrectionCard
                          key={f.id}
                          pid={pid}
                          packageId={selected.id}
                          flag={f}
                          readOnly={!isOwner}
                          onResolve={() =>
                            resolveFlag.mutate(f.id, {
                              onSuccess: () => toast.success(t("Flag resolved")),
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* objects in scope */}
                <div>
                  <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {t("Objects in scope")}
                  </div>
                  <div className="overflow-hidden rounded-lg border">
                    {(detailQ.data?.objects ?? []).map((o) => (
                      <div
                        key={o.obj_name}
                        className="flex items-center justify-between border-b px-3 py-2 text-xs last:border-0"
                      >
                        <span className="font-mono">{o.obj_name}</span>
                        <span className="text-muted-foreground">
                          {o.wricef ?? "-"} · {o.status} · {o.progress_pct}%
                        </span>
                      </div>
                    ))}
                    {!detailQ.data?.objects?.length && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        {t("No objects.")}
                      </div>
                    )}
                  </div>
                </div>

                {/* generated artifacts (N↔N) */}
                {(artifactsQ.data?.items?.length ?? 0) > 0 && (
                  <div>
                    <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t("Generated artifacts")}
                    </div>
                    <div className="space-y-2 rounded-lg border p-3">
                      {(artifactsQ.data?.items ?? []).map((a) => (
                        <div key={a.id} className="text-xs">
                          <span className="font-mono font-semibold">{a.name}</span>
                          {a.artifact_type && (
                            <span className="text-muted-foreground"> · {a.artifact_type}</span>
                          )}
                          <span className="text-muted-foreground">
                            {" "}
                            · {tp(t, a.object_ids.length, "1 source object", "{{n}} source objects")}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* consultant corrections + re-release (CC.6) */}
                <ContextCuration pid={pid} packageId={selected.id} readOnly={!isOwner} />

                {/* context bundle */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t("Context bundle")}
                      {contextQ.data && (
                        <span className="ml-1.5 font-normal normal-case text-muted-foreground">
                          v{contextQ.data.version}
                        </span>
                      )}
                    </span>
                    {contextQ.data?.bundle_path && (
                      <a
                        href={`/api/projects/${pid}/code-companion/packages/${selected.id}/context/bundle`}
                        className="rounded-md border px-2.5 py-1 text-[11px] font-semibold text-primary"
                      >
                        {t("Download bundle")}
                      </a>
                    )}
                  </div>
                  {contextQ.data ? (
                    <div className="space-y-2">
                      {contextQ.data.change_summary && (
                        <div
                          className="rounded-md px-3 py-2 text-[11px]"
                          style={{ background: `hsl(var(--info) / 0.08)` }}
                        >
                          <b>{t("Last change")}:</b> {contextQ.data.change_summary}
                        </div>
                      )}
                      <div className="overflow-hidden rounded-lg border">
                        <div className="border-b bg-secondary px-3 py-1.5 font-mono text-[10px] font-bold text-muted-foreground">
                          context.md
                        </div>
                        <pre className="max-h-48 overflow-auto whitespace-pre-wrap p-3 font-mono text-[11px] leading-relaxed">
                          {contextQ.data.markdown}
                        </pre>
                      </div>
                      <div className="overflow-hidden rounded-lg border">
                        <div className="border-b bg-secondary px-3 py-1.5 font-mono text-[10px] font-bold text-muted-foreground">
                          CLAUDE.md
                        </div>
                        <pre className="max-h-40 overflow-auto whitespace-pre-wrap p-3 font-mono text-[11px] leading-relaxed">
                          {contextQ.data.claude_md}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed p-3 text-center text-[11px] text-muted-foreground">
                      {t("Context not built yet. Build it to release to the dev.")}
                    </div>
                  )}
                </div>

                {/* ready (owner) */}
                {isOwner && (
                  <div className="space-y-2">
                    {/* F5: o que falta aparece ANTES do clique. Antes a unica forma
                        de descobrir era tentar promover e ler a recusa. */}
                    <GatePanel projectId={pid} packageId={selected.id} />
                    {/* F8: o dossie append-only do pacote. Fica junto do gate
                        porque as duas perguntas sao a mesma no fim: "posso
                        promover?" e "com base em que?". */}

                    {/* F4 da onda 4: o gate diz o que falta agora; isto diz o que ele
                        respondeu das outras vezes, inclusive nas recusas. */}

                    <ReadyForm
                      onSubmit={(loc) =>
                        ready.mutate(
                          { packageId: selected.id, deployLocation: loc },
                          {
                            onSuccess: () => toast.success(t("Marked ready for deploy")),
                            // O backend recusa com 409 e a lista do que falta. Mostrar
                            // "nao deu" e jogar fora o motivo empurra o dev pro MCP.
                            onError: (err) => {
                              const detail = (
                                err as {
                                  response?: {
                                    data?: { detail?: { missing?: string[] } | string };
                                  };
                                }
                              )?.response?.data?.detail;
                              const missing =
                                typeof detail === "object" ? detail?.missing : undefined;
                              toast.error(
                                missing?.length
                                  ? t("Not ready: {{reasons}}", {
                                      reasons: missing.slice(0, 3).join("; "),
                                    })
                                  : t("Could not mark ready"),
                              );
                            },
                          },
                        )
                      }
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ReadyForm({ onSubmit }: { onSubmit: (location: string) => void }) {
  const { t } = useTranslation();
  const [loc, setLoc] = useState("");
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {t("Ready for deploy")}
      </div>
      <div className="flex gap-2">
        <input
          value={loc}
          onChange={(e) => setLoc(e.target.value)}
          placeholder={t("Code location (bucket path)")}
          className="flex-1 rounded-md border bg-background px-2.5 py-1.5 text-sm"
        />
        <Button size="sm" disabled={!loc.trim()} onClick={() => onSubmit(loc.trim())}>
          {t("Mark ready")}
        </Button>
      </div>
    </div>
  );
}

// CC.6 — flag com correção inline (resolve levando o conhecimento ao contexto).
function FlagCorrectionCard({
  pid,
  packageId,
  flag,
  onResolve,
  readOnly = false,
}: {
  pid: string;
  packageId: number;
  flag: CcFlag;
  onResolve: () => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const add = useCcAddSupplement(pid, packageId);

  return (
    <div className="rounded-lg border p-3" style={{ background: `hsl(var(--critical) / 0.06)` }}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold" style={{ color: `hsl(var(--critical))` }}>
          {t(flag.kind)}
        </span>
        {!readOnly && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>
              {t("Correct")}
            </Button>
            <Button size="sm" variant="outline" onClick={onResolve}>
              {t("Resolve")}
            </Button>
          </div>
        )}
      </div>
      {flag.note && <div className="mt-1.5 text-xs text-foreground">{flag.note}</div>}
      {open && (
        <div className="mt-2 space-y-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("Correction title (e.g. SEND_SMS contract)")}
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("The knowledge only you have: external FM contract, table name, business rule…")}
            rows={3}
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm"
          />
          <Button
            size="sm"
            disabled={!title.trim() || !body.trim() || add.isPending}
            onClick={() =>
              add.mutate(
                { title: title.trim(), body: body.trim(), flag_id: flag.id },
                {
                  onSuccess: () => {
                    toast.success(t("Correction saved and flag resolved. Re-release to publish."));
                    setOpen(false);
                    setTitle("");
                    setBody("");
                  },
                  onError: () => toast.error(t("Could not save the correction")),
                },
              )
            }
          >
            {t("Save correction & resolve")}
          </Button>
        </div>
      )}
    </div>
  );
}

// CC.6 — histórico de correções + re-liberação (rebuild) do contexto.
function ContextCuration({
  pid,
  packageId,
  readOnly = false,
}: {
  pid: string;
  packageId: number;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const suppQ = useCcSupplements(pid, packageId);
  const rebuild = useCcRebuildContext(pid, packageId);
  const items = suppQ.data?.items ?? [];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Consultant corrections")} · {items.length}
        </span>
        {!readOnly && (
          <Button
            size="sm"
            disabled={rebuild.isPending}
            onClick={() =>
              rebuild.mutate(undefined, {
                onSuccess: (c) =>
                  toast.success(t("Context re-released · v{{v}}", { v: c.version })),
                onError: () => toast.error(t("Could not re-release the context")),
              })
            }
          >
            {rebuild.isPending ? t("Releasing…") : t("Re-release context")}
          </Button>
        )}
      </div>
      {items.length > 0 ? (
        <div className="space-y-2 rounded-lg border p-3">
          {items.map((s) => (
            <div key={s.id} className="text-xs">
              <div className="font-semibold">
                {s.title}
                {s.flag_id && <span className="text-muted-foreground"> · flag #{s.flag_id}</span>}
              </div>
              <div className="mt-0.5 whitespace-pre-wrap text-muted-foreground">{s.body}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-3 text-center text-[11px] text-muted-foreground">
          {t("No corrections yet. Add one from a flag to enrich the context.")}
        </div>
      )}
    </div>
  );
}

type CcTab = "board" | "trace" | "evidence" | "devs" | "setup";

/**
 * O pacote em foco, compartilhado entre o board e as tabs de Trace e Evidence.
 *
 * Vive aqui e não dentro do board porque as três tabs falam do mesmo pacote: clicar num
 * card no board é escolher o assunto, e Trace e Evidence são duas leituras dele. Sem
 * isso cada tab precisaria do próprio seletor, e três seletores para uma escolha só é
 * como eles saem de sincronia.
 */
export const CcFocusContext = createContext<{
  focused: CcPackageSummary | null;
  focus: (p: CcPackageSummary | null) => void;
  goTo: (t: "board" | "trace" | "evidence") => void;
}>({ focused: null, focus: () => {}, goTo: () => {} });

export default function CodeCompanion() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [tab, setTab] = useState<CcTab>("board");
  const [focused, setFocused] = useState<CcPackageSummary | null>(null);

  const tabs: { id: CcTab; label: string; icon: typeof FolderKanban; badge?: string }[] = [
    { id: "board", label: t("Migration Board"), icon: FolderKanban },
    // Trace e Evidence existem SEMPRE. A primeira versão as escondia até haver pacote
    // em foco, e o efeito foi pior que o problema: a barra de navegação mudava de forma
    // debaixo de quem usa, com as tabs surgindo do nada e as vizinhas trocando de lugar.
    // Sem foco elas abrem num seletor de pacote — que não é uma tab vazia, é a escolha
    // que falta. O badge diz qual pacote está em foco.
    { id: "trace", label: t("Trace"), icon: Activity, badge: focused?.devclass },
    { id: "evidence", label: t("Evidence"), icon: ScrollText, badge: focused?.devclass },
    { id: "devs", label: t("Devs & Teams"), icon: Users },
    { id: "setup", label: t("Developer Setup"), icon: Plug },
  ];

  return (
    <div>
      <div className="border-b px-6">
        {/* `tablist` de verdade: o estado ativo era só cor mais um traço de 2px, e
            leitor de tela não tinha como saber qual tab estava aberta. */}
        <nav className="flex gap-1" role="tablist" aria-label={t("Code Companion")}>
          {tabs.map((x) => {
            const Icon = x.icon;
            const active = tab === x.id;
            return (
              <button
                key={x.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(x.id)}
                className={
                  "relative flex items-center gap-2 px-3 py-3 text-sm font-semibold transition-colors " +
                  (active ? "text-primary" : "text-muted-foreground hover:text-foreground")
                }
              >
                <Icon className="h-4 w-4" />
                {x.label}
                {x.badge && (
                  <span
                    className="rounded-full px-1.5 py-0.5"
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "9px",
                      color: "var(--exed-ink-400)",
                      background: "var(--exed-ink-50)",
                    }}
                  >
                    {x.badge}
                  </span>
                )}
                {active && (
                  <span
                    className="absolute inset-x-2 -bottom-px h-0.5 rounded-full"
                    style={{ background: "hsl(var(--accent))" }}
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      <CcFocusContext.Provider
        value={{
          focused,
          focus: setFocused,
          goTo: (destino) => setTab(destino),
        }}
      >
        {/* Trace desenha o próprio hero de ponta a ponta, então ele não leva o padding
            da moldura — é a única tab que sangra até a borda. */}
        {tab === "trace" && focused ? (
          <TraceScreen
            projectId={projectId!}
            pkg={focused}
            onOpenEvidence={() => setTab("evidence")}
          />
        ) : (
          <div className="p-6">
            {tab === "board" && <BoardScreen />}
            {/* Sem foco, as duas tabs oferecem a escolha em vez de um beco. */}
            {(tab === "trace" || tab === "evidence") && !focused && (
              <PackagePicker
                projectId={projectId!}
                titulo={tab === "trace" ? t("Trace") : t("Evidence")}
                onPick={setFocused}
              />
            )}
            {tab === "evidence" && focused && (
              <EvidenceScreen projectId={projectId!} pkg={focused} />
            )}
            {tab === "devs" && <DevsScreen />}
            {tab === "setup" && <SetupScreen />}
          </div>
        )}
      </CcFocusContext.Provider>
    </div>
  );
}
